import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';

import { GasFormBaseComponent } from '../../gas-form-base-component';
import { GasStatusTag, GasStatusTagType, GasStatusTagTypeEnum } from '../gas-status-tag.interface';
import { GasForm } from '../../gas-form.namespace';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * @description альтернатива checkbox group / radio group
 *
 * @example
 * <gas-status-tag-group
 *    label="Статус голосования"
 *    [gasStatusTagItems]="[{ label: 'Запущено', value: 'started'  }, { label: 'Завершено', value: 'completed'}]"
 *    [formControl]="votingForm"
 * >
 * </gas-status-tag-group>
 *
 * @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=8222%3A824&viewport=2136%2C-1177%2C0.5202205777168274
 */
@UntilDestroy()
@Component({
  selector: 'gas-status-tag-group',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasStatusTagGroupComponent extends GasFormBaseComponent implements AfterViewInit {

  @Input()
  public gasStatusTagItems: GasStatusTag<unknown>[] = [];

  @Input()
  public label: string;

  @Input()
  public type: GasStatusTagType = GasStatusTagTypeEnum.Radio;

  @Output()
  public itemSelected = new EventEmitter<unknown[]>();

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    @Self() @Optional() private ngControl: NgControl,
  ) {
    super(changeDetectorRef, elementRef, renderer);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngAfterViewInit(): void {
    if (this.ngControl) {
      this.form = this.ngControl.control as FormControl;
    }

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => this.changeDetectorRef.detectChanges());

    if (this.form.value && !Array.isArray(this.form.value)) {
      this.form.setValue(
        this.gasStatusTagItems
          .find((statusTag: GasStatusTag<unknown>) => statusTag.value === this.form.value) !== undefined
          ? [this.form.value]
          : [],
      );
    }
  }

  public isActive(value: unknown): boolean {
    if (Array.isArray(this.form.value)) {
      return this.form.value.find((formValue: unknown) => formValue === value) !== undefined;
    }
    return false;
  }

  public writeValue(value: GasForm.GasFilterFormBaseValue): void {
    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
    }
  }

  public selectItem(statusTag: GasStatusTag<unknown>): void {
    this.form.setValue(
      this.type === GasStatusTagTypeEnum.Radio
        ? this.getRadioTypeValue(statusTag)
        : this.getCheckboxTypeValue(statusTag),
    );

    this.itemSelected.emit(this.form.value);
  }

  private getRadioTypeValue(statusTag: GasStatusTag<unknown>): unknown[] {
    return this.isActive(statusTag.value) ? [] : [statusTag.value];
  }

  private getCheckboxTypeValue(statusTag: GasStatusTag<unknown>): unknown[] {
    return this.isActive(statusTag.value)
      ? this.form.value.filter(item => item !== statusTag.value)
      : [...this.form.value || [], ...[statusTag.value]];
  }
}
